import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Empty } from "antd";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

type LocationItem = {
  city: string;
  state: string;
  sentiment: string;
  score: number | null;
  channel: string;
  reason: string;
  lat?: number | null;
  lng?: number | null;
};

type GeoItem = LocationItem & { lat: number; lng: number };

const sentimentColor = (s: string) => {
  if (!s) return "#888";
  const l = s.toLowerCase();
  if (l === "positive") return "#22c55e";
  if (l === "negative") return "#ef4444";
  return "#f59e0b";
};

const makeIcon = (sentiment: string) =>
  L.divIcon({
    className: "",
    html: `<div style="
      width:14px;height:14px;border-radius:50%;
      background:${sentimentColor(sentiment)};
      border:2.5px solid white;
      box-shadow:0 1px 5px rgba(0,0,0,0.35);
      pointer-events:none;
    "></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });

// Coordinates are resolved server-side (backend geocoding). A row is mappable
// only when both lat and lng are finite numbers; unresolvable rows come back
// with lat/lng = null and are shown in the fallback list instead.
const hasCoords = (d: LocationItem): d is GeoItem =>
  Number.isFinite(d.lat as number) && Number.isFinite(d.lng as number);

type Props = { data: LocationItem[] };

const LocationMapCard: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation("dashboardModals");
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    const handleBeforePrint = () => mapRef.current?.invalidateSize();
    window.addEventListener("beforeprint", handleBeforePrint);
    return () => window.removeEventListener("beforeprint", handleBeforePrint);
  }, []);

  if (!data?.length) {
    return (
      <Empty
        description={t("locationMapCard.noData")}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const geoItems = data.filter(hasCoords);

  // The PDF export can't reliably capture the live Leaflet tiles (OpenStreetMap
  // serves them without CORS headers, which taints the canvas html2canvas needs
  // to read), so it always renders this list snapshot instead of the map —
  // hidden on screen, shown only while the export is capturing this card.
  const renderList = (items: LocationItem[]) => (
    <div style={{ padding: 12, display: "grid", gap: 8 }}>
      {items.slice(0, 12).map((d, i) => (
        <div
          key={i}
          style={{
            padding: "8px 12px",
            border: "1px solid #f0f0f0",
            borderRadius: 10,
            background: "#fafafa",
            borderLeft: `4px solid ${sentimentColor(d.sentiment)}`,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {d.city}
            {d.state ? `, ${d.state}` : ""}
          </div>
          <div
            style={{
              marginTop: 4,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              fontSize: 12,
              color: "#666",
            }}
          >
            {d.sentiment && (
              <span>
                {t("locationMapCard.sentimentLabel")} {d.sentiment}
              </span>
            )}
            {d.score !== null && (
              <span>
                {t("locationMapCard.csatLabel")} {d.score}
              </span>
            )}
            {d.channel && (
              <span>
                {t("locationMapCard.channelLabel")} {d.channel}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (geoItems.length > 0) {
    const avgLat = geoItems.reduce((s, d) => s + d.lat, 0) / geoItems.length;
    const avgLng = geoItems.reduce((s, d) => s + d.lng, 0) / geoItems.length;
    const unresolved = data.length - geoItems.length;

    return (
      <div>
        <div className="card-live-view">
          <div style={{ height: 280, borderRadius: 8, overflow: "hidden" }}>
            <MapContainer
              ref={mapRef}
              center={[avgLat, avgLng]}
              zoom={5}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {geoItems.map((d, i) => (
                <Marker
                  key={i}
                  position={[d.lat, d.lng]}
                  icon={makeIcon(d.sentiment)}
                >
                  <Popup>
                    <div
                      style={{
                        minWidth: 150,
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 13,
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>
                        {d.city}
                        {d.state ? `, ${d.state}` : ""}
                      </div>
                      {d.sentiment && (
                        <div>
                          {t("locationMapCard.sentimentLabel")}{" "}
                          <span
                            style={{
                              color: sentimentColor(d.sentiment),
                              fontWeight: 600,
                            }}
                          >
                            {d.sentiment}
                          </span>
                        </div>
                      )}
                      {d.score !== null && (
                        <div>
                          {t("locationMapCard.csatLabel")}{" "}
                          <strong>{d.score}</strong>
                        </div>
                      )}
                      {d.channel && (
                        <div>
                          {t("locationMapCard.channelLabel")} {d.channel}
                        </div>
                      )}
                      {d.reason && (
                        <div style={{ marginTop: 4, color: "#666" }}>
                          {d.reason}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          {unresolved > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#999" }}>
              {t("locationMapCard.unresolvedLocations", { count: unresolved })}
            </div>
          )}
        </div>
        <div className="card-pdf-snapshot" style={{ display: "none" }}>
          {renderList(data)}
        </div>
      </div>
    );
  }

  // Fallback list when the backend returned no coordinates
  // (geocoding disabled, or none of the locations were resolvable).
  return renderList(data);
};

export default LocationMapCard;
